import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

import {
  criteriaPayload,
  emptyCriteria,
  ProfileCriteriaFields,
  type ProfileCriteriaValue,
} from './generational-group-criteria-fields';
import { GroupsSubnav } from './groups-subnav';
import { GroupLargestCharts } from './group-largest-charts';

interface GroupRow {
  id: string;
  name: string;
  type: 'manual' | 'template';
  isActive: boolean;
  memberCount: number;
  linkedGroupId: string | null;
  linkedGroupName: string | null;
  linkedGroupType: 'manual' | 'template' | null;
}

interface ChurchGroupInsights {
  groupCount: number;
  membershipCount: number;
  largest: Array<{ key: string; label: string; value: number }>;
  largestManual: Array<{ key: string; label: string; value: number }>;
  largestAutoFilled: Array<{ key: string; label: string; value: number }>;
}

export const GenerationalGroupsPage = () => {
  const { t } = useTranslation('groups');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [criteria, setCriteria] = useState<ProfileCriteriaValue>(emptyCriteria);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const canManage = allowsFeature('smart_groups');

  const list = useQuery({
    queryKey: ['groups', 'template', page, pageSize],
    queryFn: () =>
      api.get<{
        data: GroupRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/groups?type=template&page=${page}&pageSize=${pageSize}`),
    enabled: canManage,
  });

  const insights = useQuery({
    queryKey: ['groups', 'insights', 'template'],
    queryFn: () => api.get<ChurchGroupInsights>('/groups/insights?type=template'),
    enabled: canManage,
  });

  const preview = useMutation({
    mutationFn: () =>
      api.post<{ count: number }>('/groups/templates/preview', {
        templateId: 'profile',
        templateParams: criteriaPayload(criteria),
      }),
    onSuccess: (result) => {
      setPreviewCount(result.count);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const resyncAll = useMutation({
    mutationFn: () => api.post<{ evaluated: true; groupCount: number }>('/groups/templates/evaluate'),
    onSuccess: () => {
      toast.success(t('autoFilled.resyncedAll'));
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<GroupRow>('/groups/smart', {
        name,
        type: 'template',
        templateId: 'profile',
        templateParams: criteriaPayload(criteria),
        isActive,
        isOpen: false,
      }),
    onSuccess: () => {
      toast.success(t('autoFilled.saved'));
      setOpen(false);
      setName('');
      setIsActive(true);
      setCriteria(emptyCriteria());
      setPreviewCount(null);
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GroupRow>[] = [
    {
      id: 'name',
      header: t('columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'members',
      header: t('columns.members'),
      cell: (row) => t('members', { count: row.memberCount }),
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? t('statusActive') : t('statusInactive')}
        </Badge>
      ),
    },
    {
      id: 'linked',
      header: t('linked.column'),
      cell: (row) =>
        row.linkedGroupName ? (
          <span className="text-body-sm text-fg-secondary">
            {t('linked.linkedManual', { name: row.linkedGroupName })}
          </span>
        ) : (
          '—'
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('autoFilled.description')}
        tipId="groups.smartVsStatic"
        headerActions={
          canManage ? (
            <>
              <Button
                type="button"
                variant="outline"
                loading={resyncAll.isPending}
                onClick={() => resyncAll.mutate()}
              >
                {t('autoFilled.resyncAll')}
              </Button>
              <Button type="button" onClick={() => setOpen(true)}>
                {t('autoFilled.create')}
              </Button>
            </>
          ) : undefined
        }
      />

      <GroupsSubnav />

      {!canManage ? (
        <div className="mb-4">
          <PlanUpgradePrompt
            title={t('nav.autoFilled')}
            description={t('upgradeSmart')}
            planNote="Professional"
          />
        </div>
      ) : (
        <>
          <CollapsibleInsights
            summary={[
              {
                label: t('insights.groups'),
                value: insights.data ? formatNumber(insights.data.groupCount) : '…',
              },
              {
                label: t('insights.memberships'),
                value: insights.data ? formatNumber(insights.data.membershipCount) : '…',
              },
            ]}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard
                label={t('insights.groups')}
                value={insights.data ? formatNumber(insights.data.groupCount) : undefined}
                icon={<Layers className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
              <StatCard
                label={t('insights.memberships')}
                value={insights.data ? formatNumber(insights.data.membershipCount) : undefined}
                icon={<Users className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            </div>

            <GroupLargestCharts
              loading={insights.isLoading}
              largestManual={insights.data?.largestManual ?? []}
              largestAutoFilled={insights.data?.largestAutoFilled ?? []}
              labels={{
                autoFilledTitle: t('insights.largestAutoFilled'),
                autoFilledEmpty: t('insights.largestAutoFilledEmpty'),
                manualTitle: t('insights.largestManual'),
                manualEmpty: t('insights.largestManualEmpty'),
              }}
            />
          </CollapsibleInsights>

          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('autoFilled.empty')}</p>}
            onRowClick={(row) => void navigate(`/groups/${row.id}`)}
          />

          {pageMeta && pageMeta.totalPages > 1 ? (
            <Pagination
              className="mt-4"
              page={pageMeta.page}
              pageSize={pageMeta.pageSize}
              totalItems={pageMeta.totalItems}
              totalPages={pageMeta.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          size="xl"
          closeLabel={tCommon('action.close')}
          className="overflow-visible"
        >
          <DialogHeader>
            <DialogTitle>{t('autoFilled.create')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
              <div className="flex flex-col gap-1.5">
                <Label>{t('name')}</Label>
                <Input value={name} onChange={(event) => setName(event.target.value)} required />
              </div>

              <ProfileCriteriaFields value={criteria} onChange={setCriteria} />

              {previewCount != null ? (
                <p className="text-body-sm text-fg-secondary">
                  {t('criteria.previewCount', { count: previewCount })}
                </p>
              ) : null}

              <SwitchField
                id="group-active"
                label={t('activeGenerational')}
                checked={isActive}
                onCheckedChange={setIsActive}
              />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => preview.mutate()}
                  loading={preview.isPending}
                >
                  {t('criteria.preview')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button type="submit" loading={create.isPending}>
                  {t('save')}
                </Button>
              </div>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
